import { v4 as uuidv4 } from "uuid";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { useLocalRadioStore } from "../stores/LocalRadioStore";
import { useStatsStore } from "../stores/statsStore";
import { RadioStation, Track } from "../types";

class RadioEngine {
  private localRadioStore = useLocalRadioStore.getState();
  private statsStore = useStatsStore.getState();
  private localLibraryStore = useLocalLibraryStore.getState();
  private cache: { [key: string]: any } = {};

  async createRadioStation(name: string, seedTracks: Track[]): Promise<RadioStation> {
    const newStation: RadioStation = {
      id: uuidv4(),
      name,
      tracks: this.expandTrackList(seedTracks),
      source: "local",
      trackCount: 0,
      createdAt: Date.now(),
    };

    newStation.trackCount = newStation.tracks.length;
    await this.localRadioStore.addRadioStation(newStation);
    return newStation;
  }

  private expandTrackList(seedTracks: Track[]): Track[] {
    const expandedTracks = [...seedTracks];
    const mostPlayedTracks = this.getCachedData("mostPlayedTracks", () => this.statsStore.getMostPlayedTracks(50));
    const topArtists = this.getCachedData("topArtists", () => this.statsStore.getMostPlayedArtists(10));

    // Add most played tracks that are not already in the seed tracks
    mostPlayedTracks.forEach(({ track }) => {
      if (!expandedTracks.some((t) => t.id === track.id)) {
        expandedTracks.push(track);
      }
    });

    // Add tracks from top artists that are not already in the expanded list
    topArtists.forEach(({ artist }) => {
      const artistTracks = this.statsStore.playHistory
        .filter((event) => event.trackData.artist === artist)
        .map((event) => event.trackData);

      artistTracks.forEach((track) => {
        if (!expandedTracks.some((t) => t.id === track.id)) {
          expandedTracks.push(track);
        }
      });
    });

    // If we still don't have enough tracks, add more from the local library
    // if (expandedTracks.length < 50) {
    //   const allLocalTracks = this.localLibraryStore.getAllAlbums().flatMap((album) => album.tracks);
    //   const shuffledTracks = this.shuffleArray(allLocalTracks);

    //   for (const track of shuffledTracks) {
    //     if (!expandedTracks.some((t) => t.id === track.id)) {
    //       expandedTracks.push(track);
    //       if (expandedTracks.length >= 50) break;
    //     }
    //   }
    // }

    return expandedTracks;
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  getNextTrack(stationId: string): Track | undefined {
    const station = this.localRadioStore.getRadioStation(stationId);
    if (!station || station.tracks.length === 0) {
      return undefined;
    }

    const listenerMetrics = this.statsStore.getListenerMetrics();
    const currentHour = new Date().getHours();
    const activeHours = this.getCachedData("activeHours", () => this.statsStore.getActiveHours());

    // Filter tracks based on listener metrics and current time
    const suitableTracks = station.tracks.filter((track) => {
      const trackPlayCount = this.getTrackPlayCount(track.id);
      const isPopularTrack = trackPlayCount > listenerMetrics.repeatPlayRate * 10;
      const isActiveHour = activeHours[currentHour] > Object.values(activeHours).reduce((a, b) => a + b, 0) / 24;

      return (isPopularTrack && isActiveHour) || Math.random() < 0.3; // 30% chance to include less popular tracks
    });

    if (suitableTracks.length === 0) {
      return station.tracks[Math.floor(Math.random() * station.tracks.length)];
    }

    // Weight tracks based on play count and skip rate
    const weightedTracks = suitableTracks.map((track) => {
      const playCount = this.getTrackPlayCount(track.id);
      const skipRate = this.getTrackSkipRate(track.id);
      const weight = (playCount + 1) * (1 - skipRate);
      return { track, weight };
    });

    // Select a track using weighted random selection
    const totalWeight = weightedTracks.reduce((sum, { weight }) => sum + weight, 0);
    let randomWeight = Math.random() * totalWeight;
    for (const { track, weight } of weightedTracks) {
      randomWeight -= weight;
      if (randomWeight <= 0) {
        return track;
      }
    }

    return weightedTracks[0].track;
  }

  private getTrackPlayCount(trackId: string): number {
    return this.statsStore.playHistory.filter((event) => event.trackId === trackId).length;
  }

  private getTrackSkipRate(trackId: string): number {
    const trackEvents = this.statsStore.playHistory.filter((event) => event.trackId === trackId);
    if (trackEvents.length === 0) return 0;
    return trackEvents.filter((event) => event.skipped).length / trackEvents.length;
  }

  async addTrackToStation(stationId: string, track: Track): Promise<void> {
    await this.localRadioStore.addTrackToRadioStation(stationId, track);
  }

  async removeTrackFromStation(stationId: string, trackId: string): Promise<void> {
    await this.localRadioStore.removeTrackFromRadioStation(stationId, trackId);
  }

  private getCachedData<T>(key: string, fetchData: () => T): T {
    if (!this.cache[key]) {
      this.cache[key] = fetchData();
    }
    return this.cache[key];
  }

  clearCache(): void {
    this.cache = {};
  }
}

export const radioEngine = new RadioEngine();
